import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { SectionHeading } from '~/components/SectionHeading'
import { attempt } from '~/api/attempt'
import { api } from '~/api/client'
import type { GenrePayload, LandingEvent } from '~/api/types'
import { AppLink } from '~/components/AppLink'
import { bubbleFor } from '~/components/bubbles'
import { CalendarDoor } from '~/components/CalendarDoor'
import { EventCard } from '~/components/EventCard'
import type { MarksActions } from '~/components/MarksContext'
import { MarksProvider } from '~/components/MarksContext'
import { useViewer } from '~/Viewer'

import s from './Genre.module.css'

/**
 * One genre: who plays it, and what they have on.
 *
 * A page rather than a filter on the calendar, which was the other way to do it. A filter is
 * a state the calendar happens to be in — you cannot send it to anybody, and leaving it is
 * indistinguishable from the filter having been wrong. A page is a place, so `/g/uk garage`
 * is a link a friend can be sent.
 *
 * The acts come first and the gigs second, deliberately. The reason to press "drum and bass"
 * on Sherelle's page is to find the other thirty-two acts, not to be shown a date list you
 * could already have got from the calendar — the dates are the second question and they are
 * underneath.
 *
 * Small by construction: MusicBrainz's genres are on 431 of 10,671 acts here, and the
 * largest tag has 33. Every one of those 33 is worth a name on a page.
 */
export function Genre() {
  const { genre = '' } = useParams()
  const [page, setPage] = useState<GenrePayload | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const me = useViewer() ?? null

  /* The gigs are cards like any other, so they carry the same three marks — and the page
     patches its own list, because only the page knows which list is on screen. */
  const touch = (patch: (e: LandingEvent) => LandingEvent) =>
    setPage(was => (was === null ? was : { ...was, events: was.events.map(patch) }))

  /**
   * Follow an act, from the list or from a card below it.
   *
   * One function for both, because the same follow is drawn twice on this page: the star in
   * the act list, and the star on any gig that act headlines. Patching one leaves the same
   * fact reading on above and off below, on one screen.
   */
  const markAct = (artistId: number, on: boolean) =>
    setPage(was =>
      was === null
        ? was
        : {
            ...was,
            acts: was.acts.map(a => (a.id === artistId ? { ...a, isFavourite: on } : a)),
            events: was.events.map(e =>
              e.act?.id === artistId ? { ...e, starred: on, picked: on || Boolean(e.picked) } : e,
            ),
          },
    )
  // Patched first, written second, and put back if the write does not take — see the note
  // on `acting` in `Highlights.tsx`, which this follows.
  const follow = (artistId: number, on: boolean) => {
    markAct(artistId, on)
    void attempt(() => api.star(artistId, on)).then(took => {
      if (!took) markAct(artistId, !on)
    })
  }

  const acting: MarksActions = {
    me,
    save: (id, on) => {
      touch(e => (e.id === id ? { ...e, liked: on } : e))
      void attempt(() => api.save(id, on)).then(took => {
        if (!took) touch(e => (e.id === id ? { ...e, liked: !on } : e))
      })
    },
    going: (id, on) => {
      touch(e => (e.id === id ? { ...e, going: on, liked: on || Boolean(e.liked) } : e))
      void attempt(() => api.going(id, on)).then(took => {
        if (!took) touch(e => (e.id === id ? { ...e, going: !on } : e))
      })
    },
    star: follow,
  }

  useEffect(() => {
    let live = true
    setPage(null)
    setFailed(null)
    api
      .genre(genre)
      .then(data => live && setPage(data))
      .catch((error: unknown) => {
        if (live) setFailed(error instanceof Error ? error.message : String(error))
      })
    return () => {
      live = false
    }
  }, [genre])

  if (failed)
    // The failure text used to be printed with it: "Nobody on this calendar plays that.
    // /genrepage/x answered 404: {"error":…}" — the API path and its JSON, at a reader who
    // asked for a kind of music. Search answers the same situation in a sentence, and the
    // artist page answers a missing act with "No such act."
    //
    // The genre is named rather than called "that", because it is usually a typo in an
    // address and the answer is more use when it shows what was read.
    return (
      <main>
        <section className={s.head}>
          <h1 className={s.name}>Nobody on this calendar plays {genre}.</h1>
          <p className={s.meta}>
            <AppLink to="/search">Search</AppLink> for an act, a room or a night instead.
          </p>
        </section>
      </main>
    )
  if (!page) return <main aria-busy="true" />

  const playing = page.acts.filter(a => a.upcoming > 0).length

  return (
    <MarksProvider value={acting}>
      <main>
        <section className={s.head}>
          <h1 className={s.name}>{page.genre}</h1>
          {/*
            * Two numbers, and the second is the one worth reading: how many of them you
            * could actually go and see. "33 acts" alone reads as a catalogue.
            */}
          <p className={s.meta}>
            {page.acts.length} {page.acts.length === 1 ? 'act' : 'acts'}
            {playing > 0 ? ` · ${playing} with dates coming up` : ' · none with dates coming up'}
          </p>
        </section>

        <section className={s.who}>
          {/* Named, because the gig cards below hold links with the same act names on
              them and "the list of acts" is a real thing a reader navigates to. */}
          <ul className={s.acts} aria-label={`Acts tagged ${page.genre}`}>
            {page.acts.map(act => (
              <li key={act.id}>
                {/*
                  * The star sits outside the link, not inside it. A button inside an anchor
                  * is invalid and behaves like one: the press follows the link as well, so
                  * following an act would leave the page you followed them from.
                  *
                  * The editor's, like every other follow — `POST /favourite` moves
                  * `curation_status` on every gig they headline, which is the shared
                  * editorial calendar, so the server 403s anybody else.
                  */}
                {me?.isEditor ? (
                  <button
                    type="button"
                    className={s.fav}
                    aria-pressed={act.isFavourite}
                    aria-label={
                      act.isFavourite
                        ? `Everything by ${act.name} is added. Undo`
                        : `Add everything by ${act.name}`
                    }
                    title={act.isFavourite ? `Following ${act.name}` : `Follow ${act.name}`}
                    onClick={() => follow(act.id, !act.isFavourite)}
                  >
                    {act.isFavourite ? '★' : '☆'}
                  </button>
                ) : null}
                <AppLink className={s.act} to={`/a/${act.id}`}>
                  <img
                    className={s.face}
                    src={act.portrait ?? bubbleFor(`a-${act.id}`)}
                    alt=""
                    loading="lazy"
                    decoding="async"
                  />
                  <b className={s.actname}>{act.name}</b>
                  <span className={s.dates}>
                    {act.upcoming > 0 ? `${act.upcoming} coming up` : 'nothing on'}
                  </span>
                </AppLink>
              </li>
            ))}
          </ul>
        </section>

        {page.events.length ? (
          <section className={s.month}>
            <div className={s.headrow}>
              <SectionHeading>What they have on</SectionHeading>
              <span className={s.meta}>
                {page.events.length} {page.events.length === 1 ? 'NIGHT' : 'NIGHTS'}
              </span>
            </div>
            <ol className={s.strip}>
              {page.events.map(event => (
                <EventCard key={event.id} event={event} />
              ))}
            </ol>
          </section>
        ) : (
          <section className={s.month}>
            {/* Not "no results". They are on the calendar, they just have nothing booked —
                which is a fact about the acts and not about the search. */}
            <p className={s.trouble}>None of them has a date in London at the moment.</p>
          </section>
        )}

        <CalendarDoor events={null} />
      </main>
    </MarksProvider>
  )
}
